import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '../ui/Button';
import { PRICING_PLANS } from '../../lib/constants';

const PACKAGE_CATEGORIES = {
    "Medical & Surgical": [
        "Bilateral femur or tibia lengthening",
        "Lengthening surgery",
        "Anesthesia Team",
        "Pain Management Team",
        "Distal IT band release",
        "Hardware (Nails/Fixators)",
        "Pathology & Radiology",
        "Clinical Management"
    ],
    "Accommodation & Living": [
        "3 months’ accommodation",
        "Airport Transfers",
        "Appointment Transfers",
        "Recovery Buddy",
        "Hospital Costs"
    ],
    "Rehabilitation": [
        "3 months Paley-trained physiotherapy",
        "3 months’ clinical follow-up",
    ]
};

export const Pricing: React.FC = () => {
    const scrollToContact = () => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section id="pricing" className="py-20 md:py-32 bg-white">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16 md:mb-24">
                    <span className="text-[#11BC97] font-bold tracking-widest uppercase text-xs md:text-sm mb-4 block flex items-center justify-center gap-2">
                        <span className="w-8 h-[1px] bg-[#11BC97]"></span>
                        Investment
                        <span className="w-8 h-[1px] bg-[#11BC97]"></span>
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 text-black">Transparent Pricing</h2>
                    <p className="text-gray-500 max-w-2xl mx-auto text-base md:text-lg font-light">
                        No hidden fees. Just world-class care.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20 md:mb-24">
                    {PRICING_PLANS.map((plan, idx) => (
                        <motion.div
                            key={plan.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1, duration: 0.6 }}
                            className="relative rounded-[2.5rem] flex flex-col h-full transition-all duration-300 group bg-white border border-gray-100 shadow-xl shadow-gray-100/50 hover:border-[#11BC97]/30 overflow-hidden"
                        >
                            {/* Image  Placeholder */}
                            {plan.image ? (
                                <div className="w-full h-80 overflow-hidden bg-gray-50 flex items-center justify-center">
                                    <img 
                                        src={plan.image} 
                                        alt={plan.title} 
                                        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 p-6"
                                    />
                                </div>
                            ) : (
                                <div className="w-full h-80" />
                            )}
                            <div className="p-8 md:p-10 flex flex-col flex-grow">
                                <div className="flex-grow">
                                    <div className="mb-8 border-b border-gray-100 pb-8 ">
                                        <h3 className="text-2xl font-bold mb-2 text-black">{plan.title}</h3>
                                        <div className="text-base text-gray-600 font-medium mb-4 space-y-4" dangerouslySetInnerHTML={{ __html: plan.description }} />
                                    </div>

                                    <div className="mb-10">
                                        {plan.price ? (
                                            <div className="text-2xl md:text-3xl font-bold mb-2 text-[#11BC97]">{plan.price} USD</div>
                                        ) : (
                                            <div className="text-lg font-semibold text-gray-500">Contact us for a quote</div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-auto">
                                    <Button 
                                        onClick={scrollToContact}
                                        variant="primary" 
                                        className="w-full py-4 text-base shadow-none bg-black hover:bg-gray-800 border-transparent"
                                    >
                                        {plan.price ? 'Get Detailed Quote' : 'Contact Us'}
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Package Includes - Bento Style Categories */}
                <div className="bg-[#F8FDFB] rounded-[2rem] md:rounded-[3rem] p-8 md:p-12 border border-[#11BC97]/10">
                    <h3 className="text-2xl md:text-3xl font-bold mb-10 md:mb-12 text-center text-black">What's Included in Our Custom Packages?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                        {Object.entries(PACKAGE_CATEGORIES).map(([category, items], idx) => (
                            <div key={idx}>
                                <h4 className="text-base md:text-lg font-bold text-[#11BC97] uppercase tracking-wider mb-6 pb-2 border-b border-[#11BC97]/20">{category}</h4>
                                <ul className="space-y-4">
                                    {items.map((item, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <div className="mt-1 w-5 h-5 rounded-full bg-[#11BC97]/20 flex items-center justify-center shrink-0">
                                                <Check className="w-3 h-3 text-[#11BC97]" />
                                            </div>
                                            <span className="text-gray-600 text-sm font-medium">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                     <div className="mt-10 md:mt-12 text-center">
                        <Button onClick={scrollToContact}>Request Full Brochure</Button>
                    </div>
                </div>
            </div>
        </section>
    );
};